import { useState, useEffect } from 'react';
import { CheckCircle, RefreshCw, Send, Bell, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { slackService, SlackConfig, SlackStatus, SlackChannel } from '../../services/slack.service';
import DogLoader from '../ui/DogLoader';
import { useAuth } from '../../contexts/AuthContext';

export default function SlackIntegration() {
  const { user } = useAuth();
  const [status, setStatus] = useState<SlackStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [showConnectForm, setShowConnectForm] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [showChannelModal, setShowChannelModal] = useState(false);
  const [loadingChannels, setLoadingChannels] = useState(false);
  const [channels, setChannels] = useState<SlackChannel[]>([]);
  const [selectedChannel, setSelectedChannel] = useState('');
  const [sendingTest, setSendingTest] = useState(false);

  // Form state
  const [botToken, setBotToken] = useState('');
  const [signingSecret, setSigningSecret] = useState('');
  const [connecting, setConnecting] = useState(false);
  const [error, setError] = useState('');

  // Notification settings
  const [notificationSettings, setNotificationSettings] = useState({
    scheduleChanges: true,
    timeOffApproved: true,
    adherenceAlerts: true,
  });

  const organizationId = user?.organizationId || '';

  useEffect(() => {
    if (organizationId) {
      loadStatus();
    }
  }, [organizationId]);

  const loadStatus = async () => {
    if (!organizationId) return;

    try {
      setLoading(true);
      const statusData = await slackService.getStatus(organizationId);
      setStatus(statusData);
    } catch (err) {
      console.error('Failed to load Slack status:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleConnect = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!organizationId) {
      setError('Organization ID not found. Please log in again.');
      return;
    }

    setConnecting(true);

    try {
      const config: SlackConfig = {
        botToken,
        signingSecret,
      };

      console.log('Connecting Slack with organizationId:', organizationId);
      const result = await slackService.connect(organizationId, config);

      if (result.success) {
        setShowConnectForm(false);
        setBotToken('');
        setSigningSecret('');
        await loadStatus();
      } else {
        setError(result.message || 'Failed to connect to Slack');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to connect to Slack');
    } finally {
      setConnecting(false);
    }
  };

  const handleLoadChannels = async () => {
    setLoadingChannels(true);
    try {
      const result = await slackService.fetchChannels(organizationId);
      if (result.success) {
        setChannels(result.channels);
        setShowChannelModal(true);
      } else {
        setError(result.message || 'Failed to load channels');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load channels');
    } finally {
      setLoadingChannels(false);
    }
  };

  const handleUpdateChannel = async () => {
    if (!selectedChannel) {
      setError('Please select a channel');
      return;
    }

    try {
      const result = await slackService.updateDefaultChannel(organizationId, selectedChannel);
      if (result.success) {
        setShowChannelModal(false);
        await loadStatus();
        alert('Default channel updated successfully!');
      } else {
        setError(result.message || 'Failed to update channel');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update channel');
    }
  };

  const handleSendTest = async () => {
    if (!status?.defaultChannel) {
      setError('Please set a default channel first');
      return;
    }

    setSendingTest(true);
    try {
      const result = await slackService.sendTestNotification(organizationId, status.defaultChannel);
      if (result.success) {
        alert('Test notification sent! Check your Slack channel.');
      } else {
        setError(result.message || 'Failed to send test notification');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to send test notification');
    } finally {
      setSendingTest(false);
    }
  };

  const handleDisconnect = async () => {
    if (!confirm('Are you sure you want to disconnect Slack?')) {
      return;
    }

    try {
      await slackService.disconnect(organizationId);
      await loadStatus();
    } catch (err) {
      setError('Failed to disconnect');
    }
  };

  if (loading) {
    return <DogLoader />;
  }

  return (
    <div className="space-y-4">
      {/* Connection Status */}
      <div className="border-2 border-gray-200 rounded-lg overflow-hidden">
        <div
          className="p-4 hover:bg-gray-50 transition-all cursor-pointer"
          onClick={() => setIsExpanded(!isExpanded)}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <div className="text-4xl">#️⃣</div>
              <div>
                <div className="font-semibold text-gray-900">Slack</div>
                <div className="text-sm text-gray-600 mt-1">
                  Send notifications to Slack channels for schedules and alerts
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-3">
              {status?.connected && (
                <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                  <CheckCircle className="w-3 h-3 mr-1" />
                  Connected
                </span>
              )}
              {isExpanded ? (
                <ChevronUp className="w-5 h-5 text-gray-400" />
              ) : (
                <ChevronDown className="w-5 h-5 text-gray-400" />
              )}
            </div>
          </div>

          {/* Connection Details */}
          {status?.connected && !isExpanded && (
            <div className="mt-4 pt-4 border-t border-gray-200">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-600">Workspace:</span>
                  <span className="ml-2 font-medium text-gray-900">{status.teamName || 'Connected'}</span>
                </div>
                {status.defaultChannel && (
                  <div>
                    <span className="text-gray-600">Default Channel:</span>
                    <span className="ml-2 font-medium text-gray-900">{status.defaultChannel}</span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Expanded Content */}
        {isExpanded && (
          <div className="border-t border-gray-200 p-4 bg-gray-50 space-y-4" onClick={(e) => e.stopPropagation()}>
            {/* Connection Details */}
            {status?.connected && (
              <div>
                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <span className="text-gray-600">Workspace:</span>
                    <span className="ml-2 font-medium text-gray-900">{status.teamName || 'Connected'}</span>
                  </div>
                  {status.defaultChannel && (
                    <div>
                      <span className="text-gray-600">Default Channel:</span>
                      <span className="ml-2 font-medium text-gray-900">{status.defaultChannel}</span>
                    </div>
                  )}
                </div>
                <div className="flex space-x-3 mb-4">
                  <button
                    onClick={() => setShowConnectForm(true)}
                    className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-white"
                  >
                    Reconfigure
                  </button>
                  <button
                    onClick={handleDisconnect}
                    className="px-4 py-2 border-2 border-red-300 text-red-700 rounded-lg font-medium hover:bg-red-50"
                  >
                    Disconnect
                  </button>
                </div>
              </div>
            )}

            {!status?.connected && !showConnectForm && (
              <button
                onClick={() => setShowConnectForm(true)}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
              >
                Connect to Slack
              </button>
            )}

            {/* Connection Form */}
            {showConnectForm && (
              <div className="border-2 border-blue-200 rounded-lg p-6 bg-white">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                  Configure Slack Integration
                </h3>

                <form onSubmit={handleConnect} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Bot User OAuth Token
                    </label>
                    <input
                      type="password"
                      value={botToken}
                      onChange={(e) => setBotToken(e.target.value)}
                      placeholder="xoxb-1234567890-1234567890123-AbCdEfGhIjKlMnOpQrStUvWx"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                      required
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      From your Slack App settings under "OAuth & Permissions"
                    </p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Signing Secret
                    </label>
                    <input
                      type="password"
                      value={signingSecret}
                      onChange={(e) => setSigningSecret(e.target.value)}
                      placeholder="a1b2c3d4e5f6g7h8i9j0k1l2m3n4o5p6"
                      className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                      required
                    />
                    <p className="text-xs text-gray-600 mt-1">
                      From your Slack App settings under "Basic Information"
                    </p>
                  </div>

                  <div className="flex justify-end space-x-3 pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        setShowConnectForm(false);
                        setError('');
                      }}
                      className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                      disabled={connecting}
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      disabled={connecting}
                    >
                      {connecting ? (
                        <>
                          <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                          Connecting...
                        </>
                      ) : (
                        'Connect to Slack'
                      )}
                    </button>
                  </div>
                </form>
              </div>
            )}

            {/* Actions (only show when connected and not showing form) */}
            {status?.connected && !showConnectForm && (
              <>
                <div className="border-2 border-gray-200 rounded-lg p-4 bg-white">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Actions</h3>

                  <div className="grid grid-cols-2 gap-4">
                    {/* Set Default Channel */}
                    <button
                      onClick={handleLoadChannels}
                      disabled={loadingChannels}
                      className="flex items-center justify-center px-4 py-3 border-2 border-blue-600 text-blue-600 rounded-lg font-medium hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {loadingChannels ? (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                          Loading...
                        </>
                      ) : (
                        <>
                          <Bell className="w-5 h-5 mr-2" />
                          Set Default Channel
                        </>
                      )}
                    </button>

                    {/* Send Test Notification */}
                    <button
                      className="flex items-center justify-center px-4 py-3 border-2 border-green-600 text-green-600 rounded-lg font-medium hover:bg-green-50 disabled:opacity-50 disabled:cursor-not-allowed"
                      onClick={handleSendTest}
                      disabled={sendingTest || !status.defaultChannel}
                    >
                      {sendingTest ? (
                        <>
                          <RefreshCw className="w-5 h-5 mr-2 animate-spin" />
                          Sending...
                        </>
                      ) : (
                        <>
                          <Send className="w-5 h-5 mr-2" />
                          Send Test Notification
                        </>
                      )}
                    </button>
                  </div>

                  <div className="mt-4 bg-blue-50 border border-blue-200 rounded p-3">
                    <p className="text-sm text-blue-900">
                      <span className="font-medium">Tip:</span> Set a default channel to receive automated notifications
                      for schedule changes, time-off approvals, and adherence alerts.
                    </p>
                  </div>
                </div>

                {/* Notification Settings */}
                <div className="border-2 border-gray-200 rounded-lg p-4 bg-white">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Notification Settings</h3>
                  <p className="text-sm text-gray-600 mb-4">
                    Configure which events trigger automated notifications to Slack
                  </p>

                  <div className="space-y-3">
                    {/* Schedule Changes */}
                    <div className="flex items-start justify-between py-3 border-b border-gray-100">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">Schedule Changes</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Notify agents when their schedules are updated or published
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notificationSettings.scheduleChanges}
                          onChange={(e) =>
                            setNotificationSettings({
                              ...notificationSettings,
                              scheduleChanges: e.target.checked,
                            })
                          }
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {/* Time Off Approved */}
                    <div className="flex items-start justify-between py-3 border-b border-gray-100">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">Time Off Approved</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Notify agents when their time off requests are approved or denied
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notificationSettings.timeOffApproved}
                          onChange={(e) =>
                            setNotificationSettings({
                              ...notificationSettings,
                              timeOffApproved: e.target.checked,
                            })
                          }
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>

                    {/* Adherence Alerts */}
                    <div className="flex items-start justify-between py-3">
                      <div className="flex-1">
                        <div className="font-medium text-gray-900">Adherence Alerts</div>
                        <div className="text-sm text-gray-600 mt-1">
                          Send real-time alerts when agents are out of adherence with their schedule
                        </div>
                      </div>
                      <label className="relative inline-flex items-center cursor-pointer ml-4">
                        <input
                          type="checkbox"
                          className="sr-only peer"
                          checked={notificationSettings.adherenceAlerts}
                          onChange={(e) =>
                            setNotificationSettings({
                              ...notificationSettings,
                              adherenceAlerts: e.target.checked,
                            })
                          }
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                      </label>
                    </div>
                  </div>

                  <div className="mt-4 pt-4 border-t border-gray-200 flex justify-end">
                    <button
                      onClick={() => {
                        // In a real app, this would save to the backend
                        alert('Notification settings saved successfully!');
                      }}
                      className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700"
                    >
                      Save Notification Settings
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        )}
      </div>

      {/* Channel Selection Modal */}
      {showChannelModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              Select Default Channel
            </h3>

            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Choose a channel for notifications
                </label>
                <select
                  value={selectedChannel}
                  onChange={(e) => setSelectedChannel(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-300 rounded-lg focus:border-blue-500 focus:outline-none"
                >
                  <option value="">Select a channel...</option>
                  {channels.map((channel) => (
                    <option key={channel.id} value={channel.id}>
                      #{channel.name} {channel.isPrivate ? '(Private)' : ''}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-600 mt-2">
                  Make sure the Lentil WFM app is added to the channel you select.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded p-3">
                <p className="text-sm text-blue-900">
                  <strong>Note:</strong> All automated notifications will be sent to this channel.
                </p>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowChannelModal(false);
                    setSelectedChannel('');
                    setError('');
                  }}
                  className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUpdateChannel}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                  disabled={!selectedChannel}
                >
                  Save Channel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 flex items-start">
          <AlertCircle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-800">{error}</div>
        </div>
      )}
    </div>
  );
}
